import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import displayError from '../../utils/ErrorHandler';
import { getActiveZoneId, setActiveZoneId } from '../../utils/zoneSession';
import { n2 } from '../../utils/fmt';

/*
  SilatItemsIn Component
  ----------------------
  Purpose: Manage receiving of approved Silat Items into stock.

  Expected Backend Endpoints (current integration):
  1. GET  /api/commit/approvedItems           -> Returns list of approved commitments with silatItem populated.
  2. (Future) PATCH /api/commitment/receive   -> To mark commitment quantities as received (not implemented yet).
     Body example: { updates: [{ id, quantityReceived }] }
  3. POST /api/stock/add                      -> Adds received quantities into stock collection.

  NOTE: For now we only simulate #2 + #3 sequentially. Adjust when backend transaction API is ready.
  TODO (Backend): Provide single transactional endpoint, e.g. POST /api/commit/receiveAndStock
        Body: { updates: [...], zoneId } that atomically:
          - updates silatItems received quantities & status
          - inserts stock movements / adds to stock
          - returns updated items & stock summary
        Then replace sequential calls in submitReceive() with that single request.
*/

const SilatItemsIn = ({ user, navigate }) => {
  const [items, setItems] = useState([]); // fetched approved silat items
  const [filteredItems, setFilteredItems] = useState([]);
  const [search, setSearch] = useState('');
  // NEW: status filters state
  const [statusFilters, setStatusFilters] = useState({
    commited: true,
    approved: true,
    'partial-received': true,
  received: true
  });
  const [selected, setSelected] = useState({}); // key: itemId -> { id, name, unit, pendingQty, receiveQty }
  const [quantityErrors, setQuantityErrors] = useState({});
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch approved silat items
  useEffect(() => {
    const fetchApproved = async () => {
      const zoneId = user?.zone?._id || getActiveZoneId();
      if (user?.zone?._id) setActiveZoneId(user.zone._id);
      if (!zoneId) return; // wait
      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/commit/approvedItems?includeAll=true`, { withCredentials: true });
        const data = res.data?.data || [];
        const mapped = data.map(c => ({
          _id: c._id,
          commitmentId: c._id,
          silatItemId: c.silatItem?._id,
          name: c.silatItem?.name,
          unit: c.silatItem?.unit,
          price: c.silatItem?.price,
          quantity: c.quantity,
          receivedQuantity: c.receivedQuantity || 0,
          status: c.status,
          jamiat: c.jamiat?.jamaat_name || c.jamiat?.jamiat_name // support both
        }));
        setItems(mapped);
        setFilteredItems(mapped);
      } catch (err) {
        const msg = displayError(err, navigate, () => {});
        setMessage(msg || 'Failed to load Silat Items');
      } finally {
        setLoading(false);
      }
    };
    fetchApproved();
  }, [user?.zone?._id, navigate]);

  // Filter
  useEffect(() => {
    const term = search.toLowerCase();
    const f = items.filter(it => {
      const matchesText = it.name?.toLowerCase().includes(term) || it.jamiat?.toLowerCase().includes(term);
      const matchesStatus = statusFilters[it.status];
      return matchesText && matchesStatus;
    });
    setFilteredItems(f);
  }, [search, items, statusFilters]);

  const toggleItem = (item, checked) => {
    setSelected(prev => {
      const copy = { ...prev };
      const id = item._id;
      if (checked) {
        const pending = item.pendingQuantity ?? (item.quantity - (item.receivedQuantity || 0));
        copy[id] = {
          id,
          name: item.name || item.item?.name,
          unit: item.unit || item.item?.unit || '',
          pendingQty: pending,
          receiveQty: pending // default to full
        };
      } else {
        delete copy[id];
        const qe = { ...quantityErrors };
        delete qe[id];
        setQuantityErrors(qe);
      }
      return copy;
    });
  };

  const changeQty = (id, value) => {
    setSelected(prev => {
      if (!prev[id]) return prev;
      const copy = { ...prev };
      copy[id] = { ...copy[id], receiveQty: value };
      return copy;
    });
    setQuantityErrors(prev => {
      const errs = { ...prev };
      const max = selected[id]?.pendingQty || 0;
      if (value <= 0 || value > max) {
        errs[id] = `Qty must be >0 and <= ${max}`;
      } else {
        delete errs[id];
      }
      return errs;
    });
  };

  // Number of distinct Silat commitment items selected
  const allSelectedCount = Object.keys(selected).length;
  // Sum of the quantities user intends to receive in this batch
  const totalReceiveQty = Object.values(selected).reduce((sum, it) => sum + (Number(it.receiveQty) || 0), 0);

  const submitReceive = async () => {
    if (Object.keys(quantityErrors).length > 0) {
      setMessage('Fix quantity errors before submitting');
      return;
    }
    if (allSelectedCount === 0) {
      setMessage('Select at least one item');
      return;
    }
    // Final guard: ensure no receiveQty > pending (in case state drift)
    for(const sel of Object.values(selected)){
      if(sel.receiveQty > sel.pendingQty){
        setMessage('One or more selected quantities exceed pending amount');
        return;
      }
    }
    setSubmitting(true);
    setMessage('');
    setSuccessMessage('');

    // Build payload for new consolidated Silat stock endpoint
    const stockItems = Object.values(selected).map(it => ({
      name: it.name,
      unit: it.unit,
      price: items.find(s => s._id === it.id)?.price,
      quantity: Number(it.receiveQty),
      commitmentId: it.id
    }));

    try {
      const zoneId = user?.zone?._id || getActiveZoneId();
      await axios.post(`${process.env.REACT_APP_API_URI}/api/stock/addSilatItemsToStock`, { zoneId, items: stockItems }, { withCredentials: true });

      setSuccessMessage('Silat items received and added to stock');
      setSelected({});
      // Refresh list
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/commit/approvedItems?includeAll=true`, { withCredentials: true });
      const data = res.data?.data || [];
      const mapped = data.map(c => ({
        _id: c._id,
        commitmentId: c._id,
        silatItemId: c.silatItem?._id,
        name: c.silatItem?.name,
        unit: c.silatItem?.unit,
        price: c.silatItem?.price,
        quantity: c.quantity,
        receivedQuantity: c.receivedQuantity || 0,
        status: c.status,
        jamiat: c.jamiat?.jamiat_name
      }));
        setItems(mapped);
        setFilteredItems(mapped);
    } catch (err) {
      const msg = displayError(err, navigate, () => {});
      setMessage(msg || 'Failed to receive items');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = (key) => {
    setStatusFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Export to Excel:
  // Sheet 1: Commitment (current filtered view columns)
  // Sheet 2: Commitment with price details (includes price, total price per row, and final grand total row)
  const exportToExcel = () => {
    try {
      // Sheet 1 (UI subset)
      const sheet1Data = filteredItems.map(it => ({
        CommitmentID: it._id,
        Item: it.name,
        Jamiat: it.jamiat || '',
        CommittedQty: n2(it.quantity),
        ReceivedQty: n2(it.receivedQuantity || 0),
        PendingQty: n2(it.quantity - (it.receivedQuantity || 0)),
        Unit: it.unit || '',
        Status: it.status
      }));

      // Sheet 2 (all commitments full detail)
      const sheet2Rows = items.map(it => {
        const priceNum = Number(it.price) || 0;
        const committed = Number(it.quantity) || 0;
        const totalPrice = priceNum * committed;
        return {
          CommitmentID: it._id,
          SilatItemID: it.silatItemId,
          Item: it.name,
          Jamiat: it.jamiat || '',
          Unit: it.unit || '',
          Price: n2(priceNum),
          CommittedQty: n2(committed),
          ReceivedQty: n2(it.receivedQuantity || 0),
          PendingQty: n2(committed - (it.receivedQuantity || 0)),
          TotalPrice: n2(totalPrice),
          Status: it.status
        };
      });
      const grandTotal = sheet2Rows.reduce((sum,r) => sum + (r.TotalPrice || 0), 0);
      const sheet2Data = [...sheet2Rows, {}, { Item: 'GRAND TOTAL', TotalPrice: n2(grandTotal) }];

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.json_to_sheet(sheet1Data);
      const ws2 = XLSX.utils.json_to_sheet(sheet2Data);
      XLSX.utils.book_append_sheet(wb, ws1, 'Commitment');
      XLSX.utils.book_append_sheet(wb, ws2, 'Commitment with price details');
      const fileName = `silat_commitments_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch(err){
      console.error('Excel export failed', err);
      setMessage('Failed to export Excel');
    }
  };

  if (loading) {
    return <div className="text-gray-500">Loading Silat Items...</div>;
  }

  return (
    <div className="pb-28">{/* bottom padding so fixed action bar not hidden behind nav */}
      <div className="mb-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
          <div className="flex flex-col w-full md:w-64">
            <label className="text-[11px] font-semibold text-gray-600 uppercase mb-1 tracking-wide">Search (Item / Jamiat)</label>
            <input
              type="text"
              placeholder="Search Silat items or Jamiat..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="p-2 border border-gray-300 rounded w-full"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Filter by Status</span>
            <div className="flex gap-2 flex-wrap">
              {['commited','approved','partial-received','received'].map(stat => {
                const active = statusFilters[stat];
                const labelMap = { commited:'Committed', approved: 'Approved', 'partial-received': 'Partial', received: 'Received' };
                return (
                  <button
                    key={stat}
                    type="button"
                    onClick={() => toggleStatus(stat)}
                    className={`text-xs px-3 py-1 rounded border font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 ${active ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                  >
                    {labelMap[stat]}
                  </button>
                );
              })}
            </div>
          </div>
          {(user?.role && user.role.toLowerCase() === 'admin') && (
            <div className="flex items-end">
              <button
                type="button"
                onClick={exportToExcel}
                className="h-9 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded shadow-sm transition-colors"
              >
                Export Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {message && <div className="text-red-600 text-sm mb-2">{message}</div>}
      {successMessage && <div className="text-green-600 text-sm mb-2">{successMessage}</div>}

      {filteredItems.length === 0 ? (
        <p className="text-gray-600">No approved Silat items found.</p>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item, idx) => {
            const id = item._id;
            const pending = item.pendingQuantity ?? (item.quantity - (item.receivedQuantity || 0));
            const isSelected = !!selected[id];
            const last4 = id?.slice(-4) || '';
            return (
              <div key={id} className="p-4 mb-3 border rounded shadow-md bg-white">
                {/* Header similar to PO card */}
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-[16px] font-semibold text-gray-700">
                    COM #00{idx + 1}
                  </h2>
                  <h2 className="text-[14px] font-semibold text-gray-600">
                    Commitment: {last4}
                  </h2>
                </div>

                {/* Select Row */}
                <div className="mb-3 border-b pb-2 w-fit">
                  <div className="flex items-center">
                    {pending <= 0 ? (
                      <span className="text-sm font-semibold text-green-600 flex items-center gap-1">
                        {/* Simple done indicator */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Done
                      </span>
                    ) : (
                      <>
                        <label className="custom-checkbox">
                          <input
                            type="checkbox"
                            onChange={e => toggleItem(item, e.target.checked)}
                            checked={isSelected}
                            disabled={pending <= 0}
                          />
                          <span className="checkmark"></span>
                        </label>
                        <span className="ml-2 text-sm font-semibold text-green-600 flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Select
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-fmb mb-1">{item.name}</h3>
                    <p className="text-xs text-gray-500">Jamiat: {item.jamiat || 'N/A'}</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs flex-1">
                    <div className="bg-gray-50 px-3 py-2 rounded">
                      <div className="text-[10px] uppercase text-gray-500">Committed</div>
                      <div className="font-semibold text-gray-700">{n2(item.quantity)} {item.unit}</div>
                    </div>
                    <div className="bg-gray-50 px-3 py-2 rounded">
                      <div className="text-[10px] uppercase text-gray-500">Received</div>
                      <div className="font-semibold text-green-600">{n2(item.receivedQuantity || 0)} {item.unit}</div>
                    </div>
                    <div className="bg-gray-50 px-3 py-2 rounded">
                      <div className="text-[10px] uppercase text-gray-500">Pending</div>
                      <div className="font-semibold text-orange-600">{n2(pending)} {item.unit}</div>
                    </div>
                    <div className="bg-gray-50 px-3 py-2 rounded">
                      <div className="text-[10px] uppercase text-gray-500">Status</div>
                      <div className="font-semibold capitalize">{item.status}</div>
                    </div>
                    {isSelected && (
                      <div className="bg-white px-3 py-2 rounded border">
                        <div className="text-[10px] uppercase text-gray-500 mb-1">Receive Now</div>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={selected[id].receiveQty}
                          onChange={e => changeQty(id, parseFloat(e.target.value) || 0)}
                          className={`p-1 border rounded w-full text-xs ${quantityErrors[id] ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {quantityErrors[id] && (
                          <span className="text-[10px] text-red-600">{quantityErrors[id]}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Bar */}
      {allSelectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-yellow-100 shadow-lg flex justify-between items-center z-40">
          <span className="text-sm font-semibold">
            {allSelectedCount} Silat {allSelectedCount === 1 ? 'item' : 'items'} selected
            {totalReceiveQty > 0 && (
              <span className="text-gray-600 ml-2">(Total Qty: {n2(totalReceiveQty)} )</span>
            )}
          </span>
          <button
            onClick={submitReceive}
            disabled={submitting || Object.keys(quantityErrors).length > 0}
            className={`px-4 py-2 rounded text-white text-sm font-medium ${submitting ? 'bg-gray-400' : 'bg-fmb hover:bg-fmb-dark'}`}
          >
            {submitting ? 'Processing...' : 'Add Items to Stock'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SilatItemsIn;
